import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { referenceLogos } from '../data/clients';
import { useLanguage } from '../context/LanguageContext';
import { tr } from '../locales/tr';
import { en } from '../locales/en';
import ParallaxStars from '../components/ui/ParallaxStars';

const Brands: React.FC = () => {
  const { language } = useLanguage();
  const t = language === 'TR' ? tr.brands : en.brands;
  const isTR = language === 'TR';

  return (
    <div className="pt-28 pb-24 bg-[#030811] min-h-screen relative overflow-hidden">
      {/* Parallax Stars Background */}
      <ParallaxStars density="normal" speed={0.8} opacity={0.7} />

      <Helmet>
        <title>{isTR ? "Birlikte Ürettiğimiz Markalar - WE24 Agency" : "Brands We Build Together - WE24 Agency"}</title>
        <meta
          name="description"
          content={
            isTR
              ? "Bugüne kadar sinematik prodüksiyon, strateji ve dijital deneyim ürettiğimiz değerli iş ortaklarımız."
              : "Valued brand partners for whom we have produced cinematic production, strategy, and digital experiences."
          }
        />
        <link rel="canonical" href={isTR ? "https://www.we24agency.com/markalar/" : "https://www.we24agency.com/en/brands/"} />
      </Helmet>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="max-w-3xl mb-12 text-center mx-auto">
          <span className="text-xs uppercase tracking-[0.25em] text-[#ff7a63] font-bold mb-2 block">
            {t.tag}
          </span>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-display tracking-tight text-white leading-tight mb-4 drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
            {t.title}
          </h1>
          <p className="text-sm sm:text-base md:text-lg text-zinc-300 leading-relaxed max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Centered Logos Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3.5 sm:gap-4 items-stretch justify-center">
          {referenceLogos.map((item) => (
            <div
              key={item.name}
              tabIndex={0}
              className="h-24 sm:h-28 bg-white/[0.04] border border-white/10 hover:border-[#ff7a63]/60 hover:bg-white/[0.08] focus-visible:ring-2 focus-visible:ring-[#ff7a63] focus-visible:outline-none backdrop-blur-md rounded-2xl p-3 sm:p-4 flex flex-col items-center justify-center text-center transition-all duration-300 group shadow-md hover:-translate-y-1 hover:shadow-2xl relative overflow-hidden"
              title={item.name}
              aria-label={item.name}
            >
              {item.isTypography ? (
                <div className="w-full h-full flex flex-col items-center justify-center text-center px-1 opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300">
                  <span className="font-display tracking-tight text-white font-bold text-xs sm:text-sm uppercase leading-tight text-center drop-shadow-sm">
                    {item.name}
                  </span>
                  {item.subtitle && (
                    <span className="text-[8px] sm:text-[9px] uppercase tracking-wider text-[#ff7a63] font-semibold mt-1 text-center">
                      {item.subtitle}
                    </span>
                  )}
                </div>
              ) : item.logo ? (
                <div className="relative w-full h-full flex items-center justify-center text-center">
                  <img
                    src={item.logo}
                    alt={item.name}
                    className="max-h-10 sm:max-h-12 max-w-[80%] w-auto h-auto object-contain opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300 mx-auto my-auto block pointer-events-none"
                    loading="lazy"
                    decoding="async"
                  />
                  <img
                    src={item.logo}
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 max-h-10 sm:max-h-12 max-w-[80%] w-auto h-auto object-contain filter brightness-0 invert opacity-50 group-hover:opacity-0 transition-all duration-300 pointer-events-none mx-auto my-auto block"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-center opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="text-xs font-semibold uppercase tracking-wider text-white text-center">
                    {item.name}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Bottom Conversion Section */}
        <div className="mt-20 p-8 sm:p-12 rounded-3xl bg-gradient-to-r from-[#071927] to-[#0c2438] border border-white/10 flex flex-col md:flex-row items-center justify-between gap-8 text-center md:text-left shadow-2xl">
          <div>
            <span className="text-xs uppercase font-bold tracking-[0.2em] text-[#ff7a63] mb-2 block">
              {isTR ? "İş Ortaklığı & Prodüksiyon" : "Partnership & Production"}
            </span>
            <h2 className="text-2xl sm:text-3xl font-display text-white font-bold">
              {isTR ? "Markanızı Yeni Seviyeye Taşıyalım" : "Let's Elevate Your Brand Presence"}
            </h2>
            <p className="text-xs sm:text-sm text-zinc-300 mt-2 max-w-xl">
              {isTR
                ? "Sinematik reklam filmleri, 4K canlı yayın reji sistemleri ve özel dijital stratejiler için stüdyomuzla iletişime geçin."
                : "Contact our creative studio for commercial production, live broadcasting, and digital brand acceleration."}
            </p>
          </div>
          <Link
            to={isTR ? "/iletisim" : "/en/contact"}
            className="px-8 py-3.5 bg-white hover:bg-zinc-200 text-zinc-950 font-bold rounded-full text-xs uppercase tracking-wider transition-all shadow-xl hover:scale-105 inline-flex items-center gap-2 shrink-0 focus-visible:ring-2 focus-visible:ring-[#ff7a63]"
          >
            <span>{isTR ? "Proje Başlatın" : "Start a Project"}</span>
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Brands;
